// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useSaveHotelProperty, HotelPropertyFull } from './hooks/useHotelManager';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  hotelAssignmentId: string | null;
  existing?: HotelPropertyFull | null;
}

export const HotelPropertyModal: React.FC<Props> = ({ open, onClose, eventId, hotelAssignmentId, existing }) => {
  const saveProperty = useSaveHotelProperty(eventId);

  const [form, setForm] = useState({
    hotel_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    distance_miles: '',
    group_rate_per_night: '',
    rate_valid_until: '',
    booking_link: '',
    star_rating: '',
    amenities: '',
    photo_url: '',
    is_primary: false,
    rooms_contracted: '',
    rooms_released_date: '',
    comp_room_ratio: '',
    attrition_pct: '',
    attrition_penalty_note: '',
    rebate_per_room: '',
    commission_pct: '',
    hotel_contact_name: '',
    hotel_contact_email: '',
    hotel_contact_phone: '',
  });

  useEffect(() => {
    if (existing) {
      setForm({
        hotel_name: existing.hotel_name || '',
        address: existing.address || '',
        city: existing.city || '',
        state: existing.state || '',
        zip: existing.zip || '',
        distance_miles: existing.distance_miles != null ? String(existing.distance_miles) : '',
        group_rate_per_night: existing.group_rate_per_night != null ? String(existing.group_rate_per_night) : '',
        rate_valid_until: existing.rate_valid_until || '',
        booking_link: existing.booking_link || '',
        star_rating: existing.star_rating != null ? String(existing.star_rating) : '',
        amenities: (existing.amenities || []).join(', '),
        photo_url: existing.photo_url || '',
        is_primary: existing.is_primary ?? false,
        rooms_contracted: existing.rooms_contracted != null ? String(existing.rooms_contracted) : '',
        rooms_released_date: existing.rooms_released_date || '',
        comp_room_ratio: existing.comp_room_ratio != null ? String(existing.comp_room_ratio) : '',
        attrition_pct: existing.attrition_pct != null ? String(existing.attrition_pct) : '',
        attrition_penalty_note: existing.attrition_penalty_note || '',
        rebate_per_room: existing.rebate_per_room != null ? String(existing.rebate_per_room) : '',
        commission_pct: existing.commission_pct != null ? String(existing.commission_pct) : '',
        hotel_contact_name: existing.hotel_contact_name || '',
        hotel_contact_email: existing.hotel_contact_email || '',
        hotel_contact_phone: existing.hotel_contact_phone || '',
      });
    }
  }, [existing]);

  const set = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.hotel_name.trim()) {
      toast.error('Hotel name is required');
      return;
    }

    const data: Record<string, unknown> = {
      event_id: eventId,
      hotel_assignment_id: hotelAssignmentId,
      hotel_name: form.hotel_name.trim(),
      address: form.address || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      distance_miles: form.distance_miles ? parseFloat(form.distance_miles) : null,
      group_rate_per_night: form.group_rate_per_night ? parseFloat(form.group_rate_per_night) : null,
      rate_valid_until: form.rate_valid_until || null,
      booking_link: form.booking_link || null,
      star_rating: form.star_rating ? parseFloat(form.star_rating) : null,
      amenities: form.amenities ? form.amenities.split(',').map((a) => a.trim()).filter(Boolean) : [],
      photo_url: form.photo_url || null,
      is_primary: form.is_primary,
      rooms_contracted: form.rooms_contracted ? parseInt(form.rooms_contracted) : null,
      rooms_released_date: form.rooms_released_date || null,
      comp_room_ratio: form.comp_room_ratio ? parseFloat(form.comp_room_ratio) : null,
      attrition_pct: form.attrition_pct ? parseFloat(form.attrition_pct) : null,
      attrition_penalty_note: form.attrition_penalty_note || null,
      rebate_per_room: form.rebate_per_room ? parseFloat(form.rebate_per_room) : null,
      commission_pct: form.commission_pct ? parseFloat(form.commission_pct) : null,
      hotel_contact_name: form.hotel_contact_name || null,
      hotel_contact_email: form.hotel_contact_email || null,
      hotel_contact_phone: form.hotel_contact_phone || null,
      rfp_status: existing?.rfp_status || 'accepted',
    };

    try {
      await saveProperty.mutateAsync({
        id: existing?.id,
        data,
        makePrimary: form.is_primary && !existing?.is_primary,
      });
      toast.success(existing ? 'Hotel updated' : 'Hotel added');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save hotel');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit Hotel' : 'Add Hotel'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Hotel Name *</Label>
            <Input value={form.hotel_name} onChange={(e) => set('hotel_name', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => set('address', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => set('state', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>ZIP</Label>
              <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Distance (mi)</Label>
              <Input type="number" step="0.1" value={form.distance_miles} onChange={(e) => set('distance_miles', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Group Rate/Night</Label>
              <Input type="number" step="0.01" value={form.group_rate_per_night} onChange={(e) => set('group_rate_per_night', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Rate Valid Until</Label>
              <Input type="date" value={form.rate_valid_until} onChange={(e) => set('rate_valid_until', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Booking Link</Label>
            <Input value={form.booking_link} onChange={(e) => set('booking_link', e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Star Rating</Label>
              <Input type="number" step="0.5" min="0" max="5" value={form.star_rating} onChange={(e) => set('star_rating', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Photo URL</Label>
              <Input value={form.photo_url} onChange={(e) => set('photo_url', e.target.value)} placeholder="https://..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Amenities (comma-separated)</Label>
            <Input value={form.amenities} onChange={(e) => set('amenities', e.target.value)} placeholder="Pool, Free Breakfast, WiFi" />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.is_primary}
              onCheckedChange={(v) => set('is_primary', !!v)}
              id="is_primary"
            />
            <Label htmlFor="is_primary">Primary hotel for this event</Label>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Contract Terms</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rooms Contracted</Label>
                <Input type="number" value={form.rooms_contracted} onChange={(e) => set('rooms_contracted', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Rooms Released Date</Label>
                <Input type="date" value={form.rooms_released_date} onChange={(e) => set('rooms_released_date', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Comp Room Ratio (1 per N)</Label>
                <Input type="number" value={form.comp_room_ratio} onChange={(e) => set('comp_room_ratio', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Attrition %</Label>
                <Input type="number" step="0.1" value={form.attrition_pct} onChange={(e) => set('attrition_pct', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Attrition Penalty Note</Label>
              <Textarea value={form.attrition_penalty_note} onChange={(e) => set('attrition_penalty_note', e.target.value)} rows={2} />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Revenue Terms</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rebate/Room ($)</Label>
                <Input type="number" step="0.01" value={form.rebate_per_room} onChange={(e) => set('rebate_per_room', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Commission %</Label>
                <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => set('commission_pct', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Hotel Contact</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={form.hotel_contact_name} onChange={(e) => set('hotel_contact_name', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={form.hotel_contact_email} onChange={(e) => set('hotel_contact_email', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.hotel_contact_phone} onChange={(e) => set('hotel_contact_phone', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saveProperty.isPending}>
            {saveProperty.isPending ? 'Saving...' : 'Save Hotel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
